import {BaseCard} from "../../Components/Cards/BaseCard";
import {useBoundStore} from "../../Store/store";
import {useEffect, useState} from "react";
import {TanStackTable} from "../../Components/Tables/TanStackTable";
import {createColumnHelper} from "@tanstack/react-table";
import {truncateString} from "../../Utils/StringHelper";
import {PrimaryButton} from "../../Components/Buttons/PrimaryButton";
import {useTranslation} from "react-i18next";
import {DangerButton} from "../../Components/Buttons/DangerButton";
import toast from "react-hot-toast";
import {SelectInput} from "../../Components/Form/SelectInput";
import {DateTimePickerInput} from "../../Components/Form/DateTimePickerInput";
import * as React from "react";
import * as yup from "yup";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {TextInput} from "../../Components/Form/TextInput";

export const MyCollaborations = () => {
    const getFreelancersWhereWorkedWith = useBoundStore((state) => state.getFreelancersWhereWorkedWith)
    const addFreelancerToBlacklist = useBoundStore((state) => state.addFreelancerToBlacklist)
    const removeFreelancerToBlacklist = useBoundStore((state) => state.removeFreelancerToBlacklist)
    const user = useBoundStore((state) => state.user);
    const freelancersWorkedWith = useBoundStore((state) => state.freelancersWorkedWith);
    const freelancersWorkedWithMetaData = useBoundStore((state) => state.freelancersWorkedWithMetaData);
    const getMyBranchesNotPaginated = useBoundStore(state => state.getMyBranchesNotPaginated);
    const branchesNotPaginated = useBoundStore(state => state.branchesNotPaginated);

    const [currentPage, setCurrentPage] = useState(1);
    const [forceNewData, setForceNewData] = useState(false);
    const [search, setSearch] = useState('')

    const schema = yup.object({
        branch_id: yup.string().nullable(),
    }).required()

    const {
        formState: {errors},
        getValues,
        setValue,
        control,
        watch
    } = useForm({
        resolver: yupResolver(schema),
        reValidateMode: 'onBlur',
        mode: 'onChange',
        defaultValues:{
            branch_id: null,
        }
    });

    const {t} = useTranslation();

    const branchId = watch('branch_id');

    useEffect(() => {
        setCurrentPage(1);
    }, [branchId, search]);

    useEffect(() => {
        if(user?.id){
            getFreelancersWhereWorkedWith(user?.id, currentPage, branchId, search)
        }
    }, [getFreelancersWhereWorkedWith, currentPage, branchId, search])

    useEffect(() => {
        setForceNewData(!forceNewData);
    }, [freelancersWorkedWith]);

    useEffect(() => {
        getMyBranchesNotPaginated();
    }, [getMyBranchesNotPaginated]);

    const handleAddToBlacklist = (freelancerId, branchId) => {
        addFreelancerToBlacklist(freelancerId, branchId).then(() => {
            toast.success(t('collaborations_module.toast_blacklist_added'));
            getFreelancersWhereWorkedWith(user?.id, currentPage)
        });
    }

    const handleRemoveFromBlacklist = (freelancerId) => {
        removeFreelancerToBlacklist(freelancerId, branchId ).then(() => {
            toast.success(t('collaborations_module.toast_blacklist_removed'));
            getFreelancersWhereWorkedWith(user?.id, currentPage)
        });
    }

    const jobFunctions = [
        {value: 'A1', label: t('functions.A1'), id: 0},
        {value: 'A2', label: t('functions.A2'), id: 1},
        {value: 'A0', label: t('functions.A0'), id: 2},
        {value: 'A3', label: t('functions.A3'), id: 3}
    ]

    const columnHelper = createColumnHelper()
    const columns = [
        columnHelper.accessor('Naam', {
            header: t('general.fields.name'),
            cell: props => truncateString(`${props.row.original?.branch?.contact_firstname ?? '-'} ${props.row.original?.branch?.contact_lastname}`),
        }),
        columnHelper.accessor('branch.company_name', {
            header: t('general.fields.company'),
            cell: props => truncateString(`${props.row.original?.branch?.company_name ?? '-'}`),
        }),
        columnHelper.accessor('branch', {
            header: t('general.fields.branch'),
            cell: props => truncateString(`${props.row.original?.customer_branch?.company_name ?? '-'}`),
        }),
        columnHelper.accessor('branch.contact_tel', {
            header: t('general.fields.tel'),
            cell: props => truncateString(`${props.row.original?.branch?.contact_tel ?? '-'}`),
        }),
        columnHelper.accessor('hourly_rate', {
            header: t('general.fields.hourly_rate'),
            cell: props => truncateString(`€ ${props.row.original?.hourly_rate ?? '-'}`),
        }),
        columnHelper.accessor('function', {
            header: t('general.fields.function'),
            cell: props => jobFunctions.find(jf => jf.value === props.row.original?.function)?.label ?? '-',
        }),
        columnHelper.accessor('actions', {
            header: t('general.fields.actions'),
            cell: props => {
                return (
                    <div className={'flex flex-row space-x-4'}>
                        <div>
                            {
                                props.row.original.is_blacklisted ?
                                    <DangerButton
                                        onClick={() => handleRemoveFromBlacklist(props.row.original.id, props.row.original.customer_branch.id)}
                                        outlined={true}
                                    >
                                        {t('general.fields.remove_from_blacklist')}
                                    </DangerButton>
                                    :
                                    <DangerButton
                                        onClick={() => handleAddToBlacklist(props.row.original.id, props.row.original.customer_branch.id)}
                                    >
                                        {t('general.fields.add_to_blacklist')}
                                    </DangerButton>
                            }
                        </div>
                    </div>
                )
            },
        })
    ]

    return (
        <div className={'grid grid-cols-1 gap-4'}>
            <BaseCard>
                <div className={'grid grid-cols-1 md:grid-cols-3 gap-4'}>
                    {
                        branchesNotPaginated.length > 1 && (
                            <SelectInput
                                name={'branch_id'}
                                options={branchesNotPaginated.map((branch) => {
                                    return {
                                        id: branch.id,
                                        value: branch.id,
                                        label: branch.company_name
                                    }
                                })}
                                setValue={setValue}
                                control={control}
                                value={getValues('branch_id') ?? null}
                                translationIndex={'general.fields.branch'}
                                className={'w-full mr-4 my-auto z-50'}
                                errors={errors}
                                clearable={true}
                            />
                        )
                    }

                    <div>
                        <label htmlFor={name}
                               className="block text-left text-sm font-medium leading-6 text-gray-900 mb-0">
                            {t('general.actions.search')}
                        </label>

                        <TextInput
                            type={'text'}
                            name={'search'}
                            translationIndex={null}
                            placeholder={t('general.actions.search')}
                            onChange={(e) => setSearch(e.target.value)}
                        >
                        </TextInput>
                    </div>
                </div>
            </BaseCard>

            <BaseCard>
                {freelancersWorkedWith?.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-8">{t('collaborations_module.no_results')}</p>
                ) : (
                    <TanStackTable
                        columns={columns}
                        tableData={freelancersWorkedWith}
                        forceNewData={forceNewData}
                        laravelPaginationMetaData={freelancersWorkedWithMetaData}
                        onChangePage={(page) => {
                            setCurrentPage(page +1)
                        }}
                    />
                )}
            </BaseCard>
        </div>
    )
}